import React, { useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  IconButton,
  Text,
  Select,
  Skeleton,
  VisuallyHidden,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { v4 as uuidv4 } from 'uuid';

import { sleep } from '../utils/core';
import { useDynamicPagination } from '../utils/usePagination';
import useBreakpoint from '../utils/useBreakpoint';
import Linkable from './Linkable';

const DataGrid = ({
  columns,
  data = [],
  idKey,
  mobileSizes = ['base', 'sm', 'md'],
  loading: isLoading = false,
  showLoading = true,
  emptyState = () => 'no results',
  pagination = {
    getCount: () => data.length,
    getData: ({ limit, offset }) => data.slice(offset, limit + offset),
  },
  pagination: { pageSize = 5 } = {},
  refreshed,
  ...props
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const {
    next,
    prev,
    jump,
    currentData,
    currentPage,
    pageStart,
    pageEnd,
    totalItems,
    maxPage,
    pageLoading,
  } = useDynamicPagination({ ...pagination, pageSize: rowsPerPage, refreshed });
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const breakpoint = useBreakpoint();
  const isMobile = mobileSizes.includes(breakpoint);
  const totalSpan = isMobile ? 3 : columns.reduce((total, item) => total + item.span, 0);
  const selectedBackgroundColor = (id, idx = 0, isHeader = false) => {
    if (id === selectedId) {
      return { bg: 'blue.100' }; // selected row
    }
    if (isMobile || isHeader) {
      return {
        bg: isMobile ? idx % 2 ? 'grey200' : 'white' : 'grey50',
      };
    }
    return {};
  };

  const loader = (fn) => async () => {
    try {
      setLoading(true);
      await fn();
    } finally {
      // allow pendings ticks to complete before lifting loading
      await sleep(1);
      setLoading(false);
    }
  };

  const HeaderCell = (label = '', start, span, cdx, id, idx) => (
    <Box
      {...selectedBackgroundColor(id, idx, true)}
      borderBottomWidth={1}
      borderBottomColor={isMobile ? idx % 2 ? 'white' : 'grey200' : 'grey200'}
      px={cdx === 0 ? 4 : 0}
      py={4}
      minWidth={0}
      gridColumn={`${start} / span ${span}`}
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      key={uuidv4()}
    >
      <Text textStyle="small2">{`${label}`.toUpperCase()}</Text>
    </Box>
  );

  const CellContainer = ({ id, colStart, span, children, idx, link }) => {
    const Container = Linkable(link);
    return (
      // @TODO - implement row-level linking instead of cell. Need to rewrite whole DataGrid potentially
      <Container
        _hover={{ outline: 'none', boxShadow: 'none' }}
        _focus={{ outline: 'none', boxShadow: 'none' }}
        _active={{ outline: 'none', boxShadow: 'none' }}
        {...selectedBackgroundColor(id, idx)}
        borderBottomWidth={1}
        borderBottomColor={isMobile ? idx % 2 ? 'white' : 'grey200' : 'grey200'}
        minWidth={0}
        gridColumn={`${colStart} / span ${span}`}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        onClick={() => setSelectedId(id !== selectedId ? id : null)}
        display="table-cell"
      >
        {children}
      </Container>
    );
  };

  const isNextPage = currentPage < maxPage;
  const isPrevPage = (currentPage - 1) > 0;

  const Footer = () => {
    return (
      <Flex
        width="100%"
        px={2}
        py={1}
        justifyContent="space-between"
        gridColumn={`1 / span ${totalSpan}`}
        overflow="hidden"
      >
        <Flex>
          <Text m={2} fontSize="sm">Show rows per page</Text>
          <Select
            width="70px"
            textStyle="bodyGrey2"
            style={{ color: 'grey700' }}
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(e.target.value);
              // go back to fist page when changing page size
              jump(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
          </Select>
        </Flex>
        <Flex alignItems="center">
          <Text fontSize="sm">
            {totalItems ? `${pageStart}-${pageEnd} of ${totalItems}` : '0 results'}
          </Text>
          <IconButton
            disabled={!isPrevPage}
            aria-label="previous page"
            icon={<ChevronLeftIcon />}
            variant="ghost"
            onClick={loader(prev)}
          />
          <IconButton
            disabled={!isNextPage}
            aria-label="next page"
            icon={<ChevronRightIcon />}
            variant="ghost"
            onClick={loader(next)}
          />
        </Flex>
      </Flex>
    );
  };

  const Header = () => {
    const headerCells = [];
    let colStart = 1;

    columns.forEach(({ label, span }, idx) => {
      headerCells.push(HeaderCell(label, colStart, span, idx));
      colStart += span;
    });

    return isMobile ? (
      <VisuallyHidden>{headerCells}</VisuallyHidden>
    ) : headerCells;
  };

  const itemKey = ({ [idKey]: key = uuidv4() } = {}) => key;
  // @TODO - do this better
  const itemLink = ({ $link }) => $link;

  const DataRow = ({ item, idx, cdx, link }) => {
    let colStart = 1;
    const id = itemKey(item);
    const row = [];
    row.push(...(isMobile ? [columns[cdx]] : columns).map(({
      dataKey,
      span,
      renderCell: CellContents = ({ [dataKey]: v }) => (<Text>{v}</Text>),
    }) => {
      const cell = (
        <CellContainer
          id={id}
          key={`${idx}-${colStart}`}
          item={item}
          colStart={isMobile ? 2 : colStart}
          span={isMobile ? 2 : span}
          idx={idx}
          link={link}
        >
          <CellContents {...{ [dataKey]: item[dataKey] }} isMobile={isMobile} />
        </CellContainer>
      );
      colStart += span;
      return cell;
    }));
    if (isMobile) {
      const { label } = columns[cdx];
      row.unshift(HeaderCell(label, 1, 1, 0, id, idx));
    }
    return row;
  };

  const FullRow = ({ key = uuidv4(), children, ...props }) => (
    <CellContainer key={key} idx={0} colStart={1} span={totalSpan} {...props}>
      <Flex justifyContent="center" style={{ paddingTop: '1em', paddingBottom: '1em' }}>
        {children}
      </Flex>
    </CellContainer>
  );

  const Rows = () => {
    if (showLoading && (loading || isLoading || pageLoading)) {
      const length = currentData.length || pageSize;
      return Array.from({ length }).map(() => (
        <CellContainer idx={0} colStart={1} span={totalSpan} key={uuidv4()}>
          <Skeleton width="100%" height="76px" m="2px" p={0} />
        </CellContainer>
      ));
    }
    if (!currentData.length) {
      return (
        <FullRow>{emptyState()}</FullRow>
      );
    }
    if (isMobile) {
      return currentData.map((item, idx) =>
        columns.map((_, cdx) =>
          <DataRow key={itemKey(item)} item={item} idx={idx} cdx={cdx} link={itemLink(item)} />));
    }
    return currentData.map((item, idx) =>
      <DataRow key={itemKey(item)} item={item} idx={idx} link={itemLink(item)} />);
  };

  return (
    <Grid
      gridTemplateColumns={isMobile ? 'none' : `repeat(${totalSpan}, 1fr [col-start])`}
      border="1px"
      borderColor="richBlack"
      overflow="hidden"
      bgColor="white"
      {...props}
    >
      <Header key={uuidv4()} />
      <Rows />
      <Footer />
    </Grid>
  );
};

export default DataGrid;
